import { useApiMutation, useApiQuery } from '../query/hooks';
import { graphql } from '../gql';
import { useModalContext } from '../modal/ModalContext';
import { ModalActions } from '../modal/components/ModalActions';
import { ModalContent } from '../modal/components/ModalContent';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { InputLabel, Radio, Skeleton, Stack, Typography, alpha } from '@mui/material';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { useSnackbar } from '../store/snackbar';
import config from '../config/config';

const listMetricDashboardQuery = graphql(`
	query dashboard_listMetric {
		listContentMetric {
			id
			metric {
				id
				name
			}
		}
	}
`);

const listDashboardMetric = graphql(`
	query dashboard_addedMetrics {
		listDashboardMetric {
			contentMetric {
				id
			}
		}
	}
`);

const createDashboardMetricMutation = graphql(`
	mutation CreateDashboardMetric($data: DashboardMetricCreateInput!) {
		createDashboardMetric(data: $data) {
			ok
			errorMessage
		}
	}
`);

export const addMetricToDashboardModalSchema = z.object({});

export const AddMetricToDashboardModal: FC<z.infer<typeof addMetricToDashboardModalSchema>> = () => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const { data, isLoading } = useApiQuery(listMetricDashboardQuery);
	const { data: dashboardMetrics, isLoading: dashboardMetricsLoading } = useApiQuery(listDashboardMetric);
	const { openSnackbar } = useSnackbar();
	const [metric, setMetric] = useState<string | null>(null);

	const { mutate: createDashboardMetric } = useApiMutation(createDashboardMetricMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.createDashboardMetric.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'metrics-widget.source.dashboard-add.success' }),
					error: intl.formatMessage({ id: 'metrics-widget.source.dashboard-add.error' }),
				},
				errorMessage: result.createDashboardMetric.errorMessage,
				openSnackbar,
				callback: closeModal,
			});
		},
	});

	const handleDashboardMetric = () => {
		createDashboardMetric({
			data: {
				contentMetric: { connect: { id: metric } },
				displayType: 'currentValue',
				dashboard: { connect: { id: config.dashboardId } },
				order: 0,
			},
		});
	};

	if (isLoading || dashboardMetricsLoading) {
		<ModalContent title={intl.formatMessage({ id: 'dashboard.metrics.add-metric' })}>
			<Stack gap={2}>
				<Skeleton width={'100%'} />
				<Skeleton width={'100%'} />
			</Stack>
		</ModalContent>;
	}

	const dashboardContentMetricsIds = dashboardMetrics?.listDashboardMetric.map((metric) => metric.contentMetric?.id);
	const fitleredMetrics = data?.listContentMetric.filter(({ id }) => dashboardContentMetricsIds?.includes(id) === false);

	return (
		<>
			<ModalContent title={intl.formatMessage({ id: 'dashboard.metrics.add-metric' })}>
				<Stack gap={1.5}>
					{fitleredMetrics?.length === 0 && (
						<Typography>
							<FormattedMessage id={'dashboard.metrics.add-metric.empty'} />
						</Typography>
					)}
					{dashboardContentMetricsIds?.length === 10 && (
						<Typography>
							<FormattedMessage id={'dashboard.metrics.add-metric.full'} />
						</Typography>
					)}
					{fitleredMetrics?.map(({ id, metric }) => (
						<InputLabel
							key={id}
							sx={{
								'&:has(input:checked)': {
									borderColor: (theme) => theme.palette.primary.main,
								},
								border: '1px solid transparent',
								px: 1.5,
								py: 1,
								borderRadius: 2,
								cursor: 'pointer',
								':hover': {
									borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
								},
							}}
						>
							<Radio name={'metric'} sx={{ display: 'none' }} onClick={() => setMetric(id)} />
							{metric?.name}
						</InputLabel>
					))}
				</Stack>
			</ModalContent>
			<ModalActions
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={handleDashboardMetric}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.add' })}
				disablePrimaryButton={metric === null || dashboardContentMetricsIds?.length === 10}
			/>
		</>
	);
};
